import React from "react";
import { GameId } from "../../../types/enums";
import { GiMeditation } from "react-icons/gi";
import { PiBrainBold } from "react-icons/pi";
import { HiPuzzlePiece } from "react-icons/hi2";

interface ResetTool {
  id: GameId;
  name: string;
  emoji: string | React.ReactNode;
  instruction: string;
  duration: string;
}

interface ResetToolsData {
  calming: ResetTool[];
  cognitive: ResetTool[];
  puzzles: ResetTool[];
}

interface ResetToolsProps {
  resetTools: ResetToolsData;
  onGameSelect: (gameId: GameId) => void;
}

const ResetTools: React.FC<ResetToolsProps> = ({
  resetTools,
  onGameSelect,
}) => {
  return (
    <div className="mt-12 sm:mt-16 relative z-10">
      <div className="text-center mb-8 sm:mb-10">
        <h3 className="text-3xl sm:text-4xl font-medium text-gray-900 mb-2">
          Reset Tools
        </h3>
        <p className="text-base text-gray-600">
          Short, calm activities to gently reset your focus
        </p>
      </div>

      <div className="bg-white rounded-3xl p-8 border border-orange-100 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">

          {/* CALMING */}
          <Category
            title="Calming"
            subtitle="Reduce stress & anxiety"
            icon={<GiMeditation className="text-3xl text-orange-500" />}
            games={resetTools.calming}
            onGameSelect={onGameSelect}
          />

          {/* COGNITIVE */}
          <Category
            title="Cognitive"
            subtitle="Reset attention & focus"
            icon={<PiBrainBold className="text-3xl text-orange-500" />}
            games={resetTools.cognitive}
            onGameSelect={onGameSelect}
          />

          {/* PUZZLES */}
          <Category
            title="Puzzles"
            subtitle="Calm your brain"
            icon={<HiPuzzlePiece className="text-3xl text-orange-500" />}
            games={resetTools.puzzles}
            onGameSelect={onGameSelect}
          />

        </div>
      </div>
    </div>
  );
};

export default ResetTools;

/* ---------------- Category Component ---------------- */

interface CategoryProps {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  games: ResetTool[];
  onGameSelect: (gameId: GameId) => void;
}

const Category: React.FC<CategoryProps> = ({
  title,
  subtitle,
  icon,
  games,
  onGameSelect,
}) => {
  return (
    <div className="bg-orange-50/30 rounded-xl p-5 border border-orange-100">
      <div className="text-center mb-4">
        <div className="flex justify-center mb-2">{icon}</div>
        <h4 className="text-lg font-medium text-gray-900">{title}</h4>
        <p className="text-xs text-gray-600">{subtitle}</p>
      </div>

      <div className="space-y-2">
        {games.map((game) => (
          <button
            key={game.id}
            onClick={() => onGameSelect(game.id)}
            className="w-full p-3 bg-white hover:bg-orange-50 rounded-lg text-left transition border border-orange-100 hover:border-orange-200"
          >
            <div className="flex items-center gap-2">
              {typeof game.emoji === "string" ? (
                <span className="text-lg">{game.emoji}</span>
              ) : (
                game.emoji
              )}

              <div className="flex-1 min-w-0">
                <div className="text-gray-900 font-medium text-sm truncate">
                  {game.name}
                </div>
                <div className="text-gray-500 text-xs">
                  {game.duration}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};